import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { useSelector } from "react-redux";
import { ArrowLeft, Briefcase, FileSpreadsheet, Users, Wallet } from "lucide-react";
import FilteredDataTable from "../../components/dashboard/FilteredDataTable";
import StatCard from "../../components/dashboard/StatCard";
import StatCardGrid from "../../components/dashboard/StatCardGrid";
import { USE_MOCK_AUTH } from "../../config/api";
import { PORTFOLIO_RECORDS_FILTER_CONFIG } from "../../config/tableFilterConfigs";
import { formatCurrency, generateUploadRecords } from "../../data/mock/dashboardData";
import { usePortfolios } from "../../context/PortfolioContext";
import { fetchPortfolioGroupRecords } from "../../services/portfolioUploadsApi";
import { showError } from "../../utils/toast";
import { personNameColumns } from "../../utils/personName";

export default function PortfolioGroupCasesPage() {
  const token = useSelector((state) => state.auth.token);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const { groupId } = useParams();
  const { getGroup, groupsLoading } = usePortfolios();
  const group = getGroup(groupId);

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const groupSlug = group?.group_slug || groupId || "";
  const hasGroup = Boolean(group);
  const uploadsKey = useMemo(
    () => (group?.uploads || []).map((upload) => String(upload.id)).join("|"),
    [group?.uploads]
  );

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!groupId) {
        if (!cancelled) {
          setRecords([]);
          setLoading(false);
        }
        return;
      }

      // Wait for portfolio context to finish its first load before deciding "not found".
      if (useLiveApi && groupsLoading && !hasGroup) {
        if (!cancelled) setLoading(true);
        return;
      }

      setLoading(true);
      setLoadError(null);

      const uploads = group?.uploads ?? [];

      try {
        if (!useLiveApi) {
          const mockRows = uploads.flatMap((upload) =>
            generateUploadRecords(upload.accounts || 0, upload.id, group?.name || "").map((row) => ({
              ...row,
              upload_id: String(upload.id),
              file_name: upload.fileName || null,
            }))
          );
          if (!cancelled) setRecords(mockRows);
          return;
        }

        if (!hasGroup) {
          // Still try group records by slug from the URL — group may exist on the API
          // even if context hydration failed or uploads list is empty.
          const rows = await fetchPortfolioGroupRecords(token, groupSlug, [], { force: true });
          if (!cancelled) {
            setRecords(
              (rows || []).map((row) => ({
                ...row,
                group_name: row.group_name || group?.name || null,
              }))
            );
            if (!(rows || []).length) {
              setLoadError(null);
            }
          }
          return;
        }

        const rows = await fetchPortfolioGroupRecords(token, groupSlug, uploads, { force: true });
        if (!cancelled) {
          setRecords(
            (rows || []).map((row) => ({
              ...row,
              group_name: row.group_name || group?.name || null,
            }))
          );
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(err.message || "Could not load group cases.");
          showError(err.message || "Could not load group cases.");
          setRecords([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
    // Intentionally omit `group` object identity — uploadsKey/groupSlug cover data changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, useLiveApi, groupId, groupSlug, uploadsKey, groupsLoading, hasGroup]);

  if (!group && !loading && !groupsLoading && records.length === 0) {
    return (
      <div className="mx-auto max-w-lg px-4 py-20 text-center">
        <p className="font-semibold text-primary">
          {loadError || "Portfolio group not found"}
        </p>
        <Link to="/dashboard/portfolios" className="mt-4 inline-block text-sm font-bold text-primary underline">
          Back to portfolios
        </Link>
      </div>
    );
  }

  const totalOwed = records.reduce((sum, row) => sum + (Number(row.amount_owed) || 0), 0);
  const totalPaid = records.reduce((sum, row) => sum + (Number(row.amount_paid) || 0), 0);
  const uploadCount = group?.uploads?.length ?? 0;

  const columns = [
    { key: "numericId", header: "ID", type: "id" },
    { key: "case_id", header: "Case ID", type: "id" },
    { key: "customer_number", header: "Customer no.", type: "phone" },
    ...personNameColumns(),
    { key: "account_number", header: "Account", type: "id" },
    { key: "outstanding", header: "Outstanding", type: "currency" },
    { key: "phone", header: "Mobile", type: "phone" },
    { key: "email", header: "Email" },
    { key: "ghana_card_number", header: "Ghana card", type: "id" },
    { key: "gender", header: "Gender" },
    { key: "date_of_birth", header: "Date of birth", type: "date" },
    { key: "country", header: "Country" },
    { key: "region", header: "Region" },
    { key: "exact_area", header: "Exact area" },
    { key: "gps_address", header: "GPS address" },
    { key: "group_name", header: "Group" },
    { key: "loan_amount", header: "Loan amount", type: "currency" },
    { key: "amount_owed", header: "Amount owed", type: "currency" },
    { key: "amount_paid", header: "Amount paid", type: "currency" },
    { key: "due_date", header: "Due date", type: "date" },
    { key: "last_payment_date", header: "Last payment", type: "date" },
    { key: "daysLate", header: "Days late", type: "number" },
  ];

  return (
    <div className="min-h-full bg-surface">
      <div className="border-b border-primary/[0.08] bg-white">
        <div className="mx-auto max-w-8xl px-4 py-5 sm:px-6 lg:px-8">
          <div className="flex min-w-0 items-start gap-3">
            <Link
              to="/dashboard/portfolios"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-primary/10 text-primary/60 transition-colors hover:bg-primary/[0.04] hover:text-primary"
            >
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-primary/35">
                Portfolio group
              </p>
              <h1 className="truncate text-xl font-bold text-primary">
                {group?.name || "Group cases"}
              </h1>
              <p className="mt-1 text-sm text-primary/45">
                Cases from this group’s upload file
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-8xl px-4 py-6 sm:px-6 lg:px-8">
        <StatCardGrid cols={3}>
          <StatCard
            label="Cases in group"
            value={records.length.toLocaleString()}
            sub={`${uploadCount.toLocaleString()} upload file${uploadCount === 1 ? "" : "s"}`}
            icon={Users}
            tone="primary"
          />
          <StatCard
            label="Total owed"
            value={formatCurrency(totalOwed)}
            icon={Wallet}
            tone="accent"
          />
          <StatCard
            label="Total paid"
            value={formatCurrency(totalPaid)}
            icon={Briefcase}
            tone="emerald"
          />
        </StatCardGrid>

        <div className="rounded-2xl border border-primary/[0.07] bg-white shadow-sm">
          <div className="border-b border-primary/[0.06] px-5 py-4 sm:px-6">
            <div className="flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4 text-primary/40" />
              <h2 className="text-sm font-bold text-primary">All group cases</h2>
            </div>
          </div>
          <div className="p-4 sm:p-5">
            {loadError ? <p className="mb-3 text-sm text-rose-600">{loadError}</p> : null}
            {loading || groupsLoading || !group ? (
              <p className="text-sm text-primary/45">Loading group cases…</p>
            ) : (
              <FilteredDataTable
                columns={columns}
                data={records}
                config={{ ...PORTFOLIO_RECORDS_FILTER_CONFIG, title: "Case filters" }}
                pageSize={15}
                maxVisibleColumns={6}
                emptyMessage="No cases found in this portfolio group."
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
